import qs from 'qs';

import type {
  LowInventoryNotificationBody,
  OfferSelectionData,
  ParityNotificationBody,
  ParityNotificationRecord,
  TriggerEmailResult,
  VendorOffer,
} from '~/types/services/parityNotifications';

import { json_headers, request } from './common';

export type {
  LowInventoryNotificationBody,
  NamedEntity,
  OfferSelectionData,
  ParityCategory,
  ParityItemInput,
  ParityNotificationBody,
  ParityNotificationRecord,
  TriggerEmailResult,
  VendorOffer,
} from '~/types/services/parityNotifications';

function getVendorOffers(vendorId: string, signal?: AbortSignal): Promise<{ result: VendorOffer[] }> {
  return request(`${window.configs.API_HOST}/api/offers?${qs.stringify({ q: vendorId, limit: 50 })}`, {
    method: 'GET',
    signal,
  });
}

function getOfferSelectionData(offerId: string, vendorId: string, signal?: AbortSignal): Promise<OfferSelectionData> {
  type PackageOptionShape = { fk_room_rate_id?: string };
  type PackageShape = { fk_room_rate_id?: string; package_options?: PackageOptionShape[] };
  type RoomRateShape = { id: string; rate_plan?: { id: string; name: string } };
  type RoomTypeShape = { id: string; name?: string; room_rates?: RoomRateShape[] };
  type PropertyShape = { id: string; name?: string; room_types?: RoomTypeShape[] };

  return Promise.all([
    request(`${window.configs.API_HOST}/api/offers/${encodeURIComponent(offerId)}`, {
      method: 'GET',
      signal,
    }) as Promise<{
      result: { packages?: PackageShape[] };
    }>,
    request(
      `${window.configs.API_HOST}/api/properties?${qs.stringify({
        id_salesforce_external: vendorId,
        include_deleted: true,
        limit: 100,
      })}`,
      { method: 'GET', signal },
    ) as Promise<{ result?: PropertyShape[] }>,
  ]).then(([offerResponse, propertiesResponse]) => {
    const packageRoomRateIds = new Set<string>();
    for (const pkg of offerResponse.result?.packages ?? []) {
      if (pkg.fk_room_rate_id) packageRoomRateIds.add(pkg.fk_room_rate_id);
      for (const option of pkg.package_options ?? []) {
        if (option.fk_room_rate_id) packageRoomRateIds.add(option.fk_room_rate_id);
      }
    }
    const shouldFilterByOffer = packageRoomRateIds.size > 0;

    const seenRatePlans = new Set<string>();
    const seenProperties = new Set<string>();
    const seenRoomTypes = new Set<string>();
    const seenRoomRates = new Set<string>();

    const ratePlans: { id: string; name: string }[] = [];
    const properties: { id: string; name: string }[] = [];
    const roomTypes: { id: string; name: string }[] = [];
    const roomRates: { id: string; name: string }[] = [];

    const appendSelectionRow = (property: PropertyShape, roomType: RoomTypeShape, roomRate: RoomRateShape) => {
      const rp = roomRate.rate_plan;
      if (!seenRoomRates.has(roomRate.id)) {
        seenRoomRates.add(roomRate.id);
        roomRates.push({ id: roomRate.id, name: rp?.name ?? roomRate.id });
      }

      if (rp?.id && !seenRatePlans.has(rp.id)) {
        seenRatePlans.add(rp.id);
        ratePlans.push({ id: rp.id, name: rp.name });
      }

      if (!seenRoomTypes.has(roomType.id)) {
        seenRoomTypes.add(roomType.id);
        roomTypes.push({ id: roomType.id, name: roomType.name ?? roomType.id });
      }

      if (!seenProperties.has(property.id)) {
        seenProperties.add(property.id);
        properties.push({ id: property.id, name: property.name ?? property.id });
      }
    };

    for (const property of propertiesResponse.result ?? []) {
      for (const roomType of property.room_types ?? []) {
        for (const roomRate of roomType.room_rates ?? []) {
          if (shouldFilterByOffer && !packageRoomRateIds.has(roomRate.id)) continue;
          appendSelectionRow(property, roomType, roomRate);
        }
      }
    }

    // Fallback to all vendor properties/rate plans when offer package links are stale/incomplete.
    if (shouldFilterByOffer && (properties.length === 0 || ratePlans.length === 0)) {
      for (const property of propertiesResponse.result ?? []) {
        for (const roomType of property.room_types ?? []) {
          for (const roomRate of roomType.room_rates ?? []) {
            appendSelectionRow(property, roomType, roomRate);
          }
        }
      }
    }

    return { ratePlans, properties, roomTypes, roomRates };
  });
}

function adminBasePath() {
  return window.configs.API_HOST + '/api/vendor/admin/notifications';
}

function vendorNotificationsPath() {
  return window.configs.API_HOST + '/api/vendor/notifications';
}

function postParityNotification(body: ParityNotificationBody): Promise<ParityNotificationRecord> {
  return request(`${adminBasePath()}/parity`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

function getUnsolvedParityNotifications(): Promise<ParityNotificationRecord[]> {
  return request(`${vendorNotificationsPath()}?q=unsolved&type=price_parity`, {
    method: 'GET',
    headers: json_headers,
  });
}

function getUnsolvedLowInventoryNotifications(): Promise<ParityNotificationRecord[]> {
  return request(`${vendorNotificationsPath()}?q=unsolved&type=low_inventory`, {
    method: 'GET',
    headers: json_headers,
  });
}

function postLowInventoryNotification(body: LowInventoryNotificationBody): Promise<ParityNotificationRecord> {
  return request(`${adminBasePath()}/low-inventory`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

function triggerNotificationEmail(notificationId: string): Promise<TriggerEmailResult> {
  return request(`${adminBasePath()}/${encodeURIComponent(notificationId)}/send-email`, {
    method: 'POST',
    headers: json_headers,
  });
}

export default {
  postParityNotification,
  postLowInventoryNotification,
  getUnsolvedParityNotifications,
  getUnsolvedLowInventoryNotifications,
  getVendorOffers,
  getOfferSelectionData,
  triggerNotificationEmail,
};
